<script setup lang="ts">
import { computed } from 'vue';
import ToolboxIcon from './Common/ToolboxIcon.vue';
import { useGlobalState } from '../../composables/useGlobalState';

const { userData } = useGlobalState();

const userRoles = computed(() => userData.value?.roles || []);
const isAdmin = computed(() => userRoles.value.includes('ROLE_ADMIN'));
const isToolAdmin = computed(() => userRoles.value.includes('ROLE_TOOL_ADMIN'));

const allTools = [
    { 
        name: 'Copy/Paste', 
        description: 'Synchronisation de presse-papier en temps réel avec chiffrement de bout en bout.', 
        href: '/toolbox/copy-paste', 
        icon: 'ClipboardIcon',
        color: 'text-blue-500',
        bg: 'bg-blue-500/10',
        requiredRole: 'ROLE_TOOL_COPYPASTE'
    },
    { 
        name: 'Todo List', 
        description: 'Gérez vos tâches quotidiennes et restez organisé.', 
        href: '/toolbox/todo', 
        icon: 'Todo',
        color: 'text-green-500',
        bg: 'bg-green-500/10',
        requiredRole: 'ROLE_TOOL_TODO'
    },
    { 
        name: 'Assistant IA', 
        description: 'Gérez vos contextes et discutez avec une IA pour vos rédactions.', 
        href: '/toolbox/ai-assistant', 
        icon: 'Bot',
        color: 'text-purple-500',
        bg: 'bg-purple-500/10',
        requiredRole: 'ROLE_TOOL_AI_ASSISTANT'
    },
    { 
        name: 'Administration', 
        description: 'Gérez les utilisateurs et les accès aux outils de la plateforme.', 
        href: '/toolbox/admin', 
        icon: 'Settings',
        color: 'text-orange-500',
        bg: 'bg-orange-500/10',
        requiredRole: 'ROLE_ADMIN'
    },
    {
        name: 'Simkl Sync',
        description: 'Synchronisez votre historique TVTime, Crunchyroll, ou autre avec Simkl.',
        href: '/toolbox/simkl-sync',
        icon: 'CloudSync',
        color: 'text-red-500',
        bg: 'bg-red-500/10',
        requiredRole: 'ROLE_TOOL_SIMKLSYNC'
    },
];

const tools = computed(() => {
    return allTools.filter(tool => {
        if (!tool.requiredRole) return true;
        if (isAdmin.value) return true;
        if (userRoles.value.includes(tool.requiredRole)) return true;
        if (isToolAdmin.value && tool.requiredRole.startsWith('ROLE_TOOL_')) return true;
        return false;
    });
});
</script>

<template>
    <div class="dashboard-view max-w-6xl mx-auto">
        <div class="mb-10">
            <h2 class="text-3xl font-extrabold tracking-tight mb-2">Bienvenue dans votre Toolbox</h2>
            <p class="text-muted-foreground text-lg">Choisissez un outil pour commencer à travailler.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <template v-for="tool in tools" :key="tool.name">
                <router-link 
                    v-if="!tool.disabled"
                    :to="tool.href" 
                    class="group relative flex flex-col p-6 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                >
                    <div :class="['h-12 w-12 rounded-lg mb-4 flex items-center justify-center transition-transform group-hover:scale-110', tool.bg]">
                        <ToolboxIcon :name="tool.icon" :size="24" :class="tool.color" />
                    </div>
                    <h3 class="text-xl font-bold mb-2 group-hover:text-primary transition-colors">{{ tool.name }}</h3>
                    <p class="text-muted-foreground leading-relaxed">{{ tool.description }}</p>
                    
                    <div class="mt-6 flex items-center text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        Ouvrir l'outil <ToolboxIcon name="ArrowRight" :size="14" class="ml-2" />
                    </div>
                </router-link>

                <div 
                    v-else
                    class="relative flex flex-col p-6 bg-muted/30 rounded-xl border border-border opacity-60 cursor-not-allowed"
                >
                    <div :class="['h-12 w-12 rounded-lg mb-4 flex items-center justify-center', tool.bg]">
                        <ToolboxIcon :name="tool.icon" :size="24" :class="tool.color" />
                    </div>
                    <h3 class="text-xl font-bold mb-2">{{ tool.name }}</h3>
                    <p class="text-muted-foreground leading-relaxed">{{ tool.description }}</p>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
.dashboard-view {
    animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
